/**
 * File input field.
 */
import {Component, Input, OnInit, Output, EventEmitter, ViewChild, ElementRef} from "@angular/core";
import {UntypedFormControl, Validators} from "@angular/forms";
import { Answer, SourceAnswerFileQuestionDTO } from "../../../../swaggerServices";
import {IAnswer, IFormField} from "../form-field-wrapper/form-field-wrapper.component";
import { RubixDocumentsService } from "../../../app/tax-returns/tax-return/rubix-documents/rubix-documents.service";
import {Subscription} from "rxjs";
import { TranslocoService} from "@jsverse/transloco";

declare var $: any;

enum FileStatus {
  Error = "Error",
  NoneSelected = "None",
  Original = "Original",
  Uploading = "Uploading",
  Updated = "Updated", // any changes from an initial state (NoneSelected or Original)
};

@Component({
    selector: "app-file-field",
    templateUrl: "./file-field.component.html",
    styleUrls: ["./file-field.component.scss"],
    standalone: false
})
export class FileFieldComponent implements OnInit {
  @Input() question: IFormField;
  @Input() myFormControl: UntypedFormControl;
  @Input() disabled?: boolean;
  @Input() tabIndex?: number = 0;
  @Input() lockOnlyDisabledValue?: boolean = false;
  @Output() answer: [Answer];

  @ViewChild('inputEl') inputEl: ElementRef;
  @ViewChild('modal') modal: ElementRef;

  fileAnswer: SourceAnswerFileQuestionDTO;
  status: FileStatus;
  docId: string;
  filename: string;
  tags: string;
  errorMessage: string;
  subscriptions: Subscription[] = [];
  modalMessage: { success?: {message?: any}, error?: {message?: string} };
  pathSeparator = "\\";

  constructor(
    private rubixDocumentsService: RubixDocumentsService,
    private translocoService: TranslocoService,) {
  }

  ngOnInit() {
    this.status = FileStatus.NoneSelected;

    if (this.myFormControl.value) {
      this.fileAnswer = JSON.parse(this.myFormControl.value);
      this.filename =  this.fileAnswer.name
      this.docId = this.fileAnswer.docId;
      this.status = FileStatus.Original;
      this.tags = this.fileAnswer.tags?.join(", ");
    }
  }

  downloadFile() {
    this.rubixDocumentsService.downloadFileQuestion(this.docId).subscribe(
      ({ fileName, fileContent }) => {
        if (!fileContent)  {
          this.errorMessage= "Files couldn't be downloaded, please try again later";
          return;
        }
        const url = URL.createObjectURL(fileContent);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
      }, (error) => {
        this.errorMessage = error.message;
      });
  }

  initFile(files: any) {
    if (!files) {
      this.filename = null;
      this.status = FileStatus.Updated;
      return;
    }

    const pattern = (/\.(csv|xlsx|xml|pdf|zip)$/i);
    const isValidType = this.question.sourceId? pattern.test(files[0].name): true;

    if (!isValidType) {
      this.translocoService.selectTranslate('please-upload-a-file-in-csv-xlsx-pdf-or-zip-format').subscribe(value =>
        this.modalMessage = {
        error: {
          message: value
        }
      });

      $(this.modal.nativeElement).modal('show');
    } else {
      const sourceFile = files[0];
      this.status = FileStatus.Uploading;
      this.errorMessage = null;
      this.myFormControl.setErrors({
        isUploading: true,
      });
      this.rubixDocumentsService.uploadFileQuestion(sourceFile, this.question.id).subscribe(
        (doc) => {
          this.status = FileStatus.Updated;
          this.filename = doc.name;

          this.fileAnswer = doc;
          this.filename =  this.fileAnswer.name
          this.docId = this.fileAnswer.docId;
          this.status = FileStatus.Original;
          this.tags = this.fileAnswer.tags?.join(", ");
          this.myFormControl.setValue(JSON.stringify(doc));

          this.myFormControl.setErrors({
            isUploading: false,
          });
      }, (error) => {
        this.status = FileStatus.Error;
        this.errorMessage = error.message;
        this.myFormControl.setErrors({});
      });
    }
  }

  removeFile() {
    this.myFormControl.reset();
    this.filename = null;
    this.status = FileStatus.Updated;
    this.inputEl.nativeElement.value = null;
  }
}

const fileFieldValidators = (q: IFormField) => {
  const validators = [];
  if (q.mandatory) validators.push(Validators.required);
  return validators;
};

export const makeFileFieldFormControl = (q: IFormField, answer?: IAnswer) => {
  const formControlValue = answer ? answer.answer : (q.default != null ? String(q.default) : "");
  q.validators = fileFieldValidators(q);
  const formControl = new UntypedFormControl(formControlValue, {validators: Validators.compose(q.validators)});
  if (!answer && formControlValue) {
    formControl.markAsDirty();
  }
  return formControl;
};
